import _ from 'lodash';
import { createSelector } from 'reselect';
import {
  selectRecentWaiversForAirline,
  selectWaiversById,
} from '../../../entities/waivers/waivers/waiversSelectors';
import {
  selectWorkItemDetails,
  selectWorkItem,
} from '../../../entities/waivers/workItems/waiverWorkItemDetailsSelectors';
import {
  editNewWaiver,
  createEditExistingWaiverAction,
  createChangeWaiverAssociationAction,
  associateWithWaiver,
  editAirlineConfiguration,
  resolveWorkItemAsWaiver,
  resolveWorkItemAsFalsePositive,
  resolveWorkItemAsNotWaiver,
  resolveWorkItemAsSuperseded,
  resolveWorkItemAsWaiverMissing,
  reopenWorkItem,
  resolveWorkItemAsNotQualifyingWaiver,
} from '../../../entities/waivers/workItems/waiverWorkItemActions';

import { State } from '../../../../redux/types';
import { Waiver } from '../../../../types/waiver';
import {
  WaiverWorkItemDetails,
  WaiverWorkItemAction,
  WaiverWorkItem,
} from '../../../../types/waiverWorkItem';

export const getActionsForRecentWaivers = (
  workItemDetails: WaiverWorkItemDetails,
  workItem: WaiverWorkItem,
  getWaivers: (airlineCode: string) => Waiver[],
  createWaiverAction: (waiver: Waiver) => WaiverWorkItemAction,
) => {
  const airlineCode = workItem.airlineCode;
  if (!airlineCode) {
    return [];
  }

  return _(getWaivers(airlineCode))
    .map(waiver => createWaiverAction(waiver))
    .value();
};

export const getNonWaiverActions = (workItem: WaiverWorkItem) => {
  const hasWaivers = workItem.associatedWaiverIds.size > 0;
  if (hasWaivers) {
    return [];
  }

  var nonWaiverActions = [
    resolveWorkItemAsNotWaiver,
    resolveWorkItemAsSuperseded,
    resolveWorkItemAsNotQualifyingWaiver,
  ];
  if (workItem.source === 'SCRAPER') {
    nonWaiverActions.push(resolveWorkItemAsFalsePositive);
  }
  return nonWaiverActions;
};

export const getChangeWaiverAssociationActions = (
  workItem: WaiverWorkItem,
  workItemDetails: WaiverWorkItemDetails,
  waiversById: { [key: number]: Waiver },
) => {
  return Array.from(workItem.associatedWaiverIds)
    .map((waiverId) => waiversById[waiverId as number])
    .filter(Boolean)
    .map((waiver: Waiver) => createChangeWaiverAssociationAction(waiver));
};

export const selectWorkItemActions: (state: State) => WaiverWorkItemAction[] = createSelector(
  [
    selectWorkItemDetails,
    selectWorkItem,
    selectRecentWaiversForAirline,
    selectWaiversById,
  ],
  (
    workItemDetails,
    workItem,
    recentWaiversForAirline,
    waiversById,
  ) => {
    if (!workItemDetails || !workItem) {
      return [];
    }
    if (workItem.state === 'CLOSED') {
      return [
        reopenWorkItem,
        ...getChangeWaiverAssociationActions(
          workItem,
          workItemDetails,
          waiversById,
        ),
      ];
    }
    if (!workItem.airlineCode) {
      return [editAirlineConfiguration, resolveWorkItemAsNotWaiver];
    }
    if (workItemDetails.waiverEnteredByAirline) {
      return [
        ...getActionsForRecentWaivers(
          workItemDetails,
          workItem,
          recentWaiversForAirline,
          associateWithWaiver,
        ),
        resolveWorkItemAsWaiverMissing,
      ];
    }

    const editExistingWaiversActions = getActionsForRecentWaivers(
      workItemDetails,
      workItem,
      recentWaiversForAirline,
      createEditExistingWaiverAction,
    );
    const nonWaiverActions = getNonWaiverActions(workItem);
    return [
      editNewWaiver,
      ...editExistingWaiversActions,
      resolveWorkItemAsWaiver,
      ...nonWaiverActions,
    ];
  },
);